'use client';

import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import CircularProgress from '@mui/material/CircularProgress';
import FormControlLabel from '@mui/material/FormControlLabel';
import Typography from '@mui/material/Typography';
import { useMutation } from '@parkchirp/api-client';
import React, { BaseSyntheticEvent, FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { UPDATE_SUBSCRIPTION_RECURRING_PAYMENTS, UPDATE_TENANT_RECURRING_PAYMENTS } from '../../queries';
import { RecurringPaymentsProps } from '../../types';
import { buildAchAuthorization, isTenantPortal } from '../../utils';
import { AchAuthorizationAcceptance } from './achAuthorizationAcceptance';
import { BankAccountForm } from './BankAccountForm';
import { CreditCardForm } from './creditCardForm';

// Copied from admin dashboard for re-use
export const RecurringPayments: FC<RecurringPaymentsProps> = ({
	useAch,
	useCreditCard,
	cognitoSub,
	profileData,
	isAutoPay,
	paymentMethodId,
	subscriptionId,
	manageRecurringPayments,
	gpiFormConfig,
	setShowSnackbar,
	tenantSubscriptionId,
	facilityId,
	refetchPaymentMethods,
	paymentProviderId,
	showAchComplianceNotice,
	achAuthorizationDetails,
	appName,
}) => {
	const [isEnabled, setIsEnabled] = useState(false);
	const [paymentType, setPaymentType] = useState({
		creditCard: false,
		bankAccount: false,
	});
	const [selectedCard, setSelectedCard] = useState<number | null>(null);
	const [selectedBank, setSelectedBank] = useState<number | null>(null);
	const [mode, setMode] = useState<'addCard' | 'addBank' | null>(null);
	const [achAuthorizationAccepted, setAchAuthorizationAccepted] = useState(false);
	const newSelectionRef = useRef<boolean>(false);

	const [updateSubscriptionRecurringPayments, { loading: updateLoading }] = useMutation(
		UPDATE_SUBSCRIPTION_RECURRING_PAYMENTS,
		{
			onCompleted: () => {
				setShowSnackbar({
					message: `This subscription's recurring payments have been updated.`,
					severity: 'success',
					autoHideDuration: 3000,
					onClose: () => {
						setShowSnackbar(null);
					},
				});
			},
		},
	);
	const [updateTenantRecurringPayments, { loading: tenantUpdateLoading }] = useMutation(
		UPDATE_TENANT_RECURRING_PAYMENTS,
		{
			onCompleted: () => {
				setShowSnackbar({
					message: `This subscription's recurring payments have been updated.`,
					severity: 'success',
					autoHideDuration: 3000,
					onClose: () => setShowSnackbar(null),
				});
			},
		},
	);

	const cards = profileData?.gpiCards;
	const bankAccounts = profileData?.gpiBankAccounts;

	const disableEdit = manageRecurringPayments === 'DISPLAY_AND_VIEW_ONLY';
	const selectedBankAccount = bankAccounts?.find((account) => account.id === selectedBank);
	const achAuthorization = useMemo(
		() =>
			isEnabled && selectedBankAccount && achAuthorizationDetails
				? buildAchAuthorization({
						recurring: true,
						accountType: selectedBankAccount.accountType,
						amountInCents: achAuthorizationDetails.recurringAmountInCents,
						paymentDueDay: achAuthorizationDetails.paymentDueDay,
						...achAuthorizationDetails,
					})
				: null,
		[achAuthorizationDetails, isEnabled, selectedBankAccount],
	);

	useEffect(() => {
		setAchAuthorizationAccepted(false);
	}, [achAuthorization]);

	useEffect(() => {
		setIsEnabled(false);
		setSelectedCard(null);
		setSelectedBank(null);
		setPaymentType({ creditCard: false, bankAccount: false });
		setIsEnabled(isAutoPay);
		setMode(null);

		const matchedCard = cards?.find((card) => card.id === paymentMethodId);
		const matchedBank = bankAccounts?.find((account) => account.id === paymentMethodId);

		if (matchedCard) {
			setSelectedCard(matchedCard.id);
			setPaymentType({ creditCard: true, bankAccount: false });
		} else if (matchedBank) {
			setSelectedBank(matchedBank.id);
			setPaymentType({ creditCard: false, bankAccount: true });
		} else {
			setIsEnabled(isAutoPay);
			setSelectedCard(null);
			setSelectedBank(null);
			setPaymentType({ creditCard: false, bankAccount: false });
		}
	}, [bankAccounts, cards, isAutoPay, paymentMethodId, subscriptionId]);

	useEffect(() => {
		if (!newSelectionRef.current) {
			setIsEnabled(isAutoPay);
			const matchedCard = cards?.find((card) => card.id === paymentMethodId);
			const matchedBank = bankAccounts?.find((account) => account.id === paymentMethodId);

			if (matchedCard) {
				setSelectedCard(matchedCard.id);
				setPaymentType({ creditCard: true, bankAccount: false });
			} else if (matchedBank) {
				setSelectedBank(matchedBank.id);
				setPaymentType({ creditCard: false, bankAccount: true });
			} else {
				setIsEnabled(isAutoPay);
				setSelectedCard(null);
				setSelectedBank(null);
				setPaymentType({ creditCard: false, bankAccount: false });
			}
		}
	}, [isAutoPay, cards, bankAccounts, paymentMethodId]);

	const handleRecurringToggle = useCallback(() => {
		setIsEnabled((prev) => !prev);
	}, []);

	const handlePaymentTypeChange = useCallback(
		(type: string, paymentId?: number) => (event: BaseSyntheticEvent) => {
			newSelectionRef.current = true;
			if (type === 'creditCard') {
				if (event.target.checked) {
					setPaymentType({
						creditCard: true,
						bankAccount: false,
					});
					setSelectedCard(paymentId || null);
					setSelectedBank(null);
				} else {
					setPaymentType({
						...paymentType,
						creditCard: false,
					});
					setSelectedCard(null);
					setMode(null);
				}
			} else if (type === 'bankAccount') {
				if (event.target.checked) {
					setPaymentType({
						creditCard: false,
						bankAccount: true,
					});
					setSelectedBank(paymentId || null);
					setSelectedCard(null);
				} else {
					setPaymentType({
						...paymentType,
						bankAccount: false,
					});
					setSelectedBank(null);
					setMode(null);
				}
			}
		},
		[paymentType],
	);

	const handlePaymentUpdate = useCallback(
		async (event: BaseSyntheticEvent) => {
			event.preventDefault();
			const selectedPaymentMethodId = paymentType.creditCard ? selectedCard : selectedBank;

			const isTenantAchUpdate = Boolean(tenantSubscriptionId && achAuthorizationDetails);
			const mutation = isTenantAchUpdate ? updateTenantRecurringPayments : updateSubscriptionRecurringPayments;
			const variables = isTenantAchUpdate
				? {
						subscriptionId,
						autoPay: isEnabled,
						paymentMethodId: isEnabled ? selectedPaymentMethodId : null,
						achAuthorizationAcceptance: achAuthorization,
					}
				: {
						facilityId,
						isFromTenantPortal: isTenantPortal,
						subscription: {
							autoPay: isEnabled,
							...(isEnabled ? { paymentMethodId: selectedPaymentMethodId } : { paymentMethodId: null }),
						},
						subscriptionId,
					};

			await mutation({
				variables: {
					...variables,
				},
				context: {
					apiVersion: 'v2',
					appName,
				},
				onError: (error) =>
					setShowSnackbar({
						message: error.message,
						severity: 'error',
						autoHideDuration: 3000,
						onClose: () => {
							setShowSnackbar(null);
						},
					}),
			});
			newSelectionRef.current = true;
		},
		[
			updateSubscriptionRecurringPayments,
			updateTenantRecurringPayments,
			setShowSnackbar,
			subscriptionId,
			paymentType,
			selectedCard,
			selectedBank,
			isEnabled,
			facilityId,
			tenantSubscriptionId,
			achAuthorizationDetails,
			achAuthorization,
			appName,
		],
	);

	return (
		<Box
			sx={{
				paddingTop: 2,
				paddingLeft: 5,
				width: '100%',
				display: 'flex',
				flexDirection: 'column',
			}}
		>
			{!isEnabled && <h3 style={{ marginBottom: 10 }}>Recurring payments is not enabled</h3>}
			{isEnabled && <h3 style={{ marginBottom: 10 }}>Recurring Payments</h3>}
			<FormControlLabel
				control={
					<Checkbox
						size='small'
						checked={isEnabled}
						onChange={handleRecurringToggle}
						disabled={disableEdit}
						data-cy='recurring-payments-enable-checkbox'
					/>
				}
				label='Enable Recurring Payments?'
			/>

			{isEnabled && (
				<>
					<h3 style={{ marginTop: 40, marginBottom: 10 }}>Choose a Payment Type</h3>
					{!useCreditCard && !useAch ? (
						<Alert severity='info' sx={{ mt: 1 }}>
							Online payments are not available for this subscription. Please contact an admin for help
							with payment options.
						</Alert>
					) : (
						<>
							{useCreditCard && (
								<FormControlLabel
									control={
										<Checkbox
											size='small'
											checked={paymentType.creditCard}
											disabled={disableEdit}
											onChange={handlePaymentTypeChange('creditCard')}
											data-cy='choose-credit-card-checkbox'
										/>
									}
									label='Credit Card'
								/>
							)}
							{useAch && (
								<FormControlLabel
									control={
										<Checkbox
											size='small'
											checked={paymentType.bankAccount}
											disabled={disableEdit}
											onChange={handlePaymentTypeChange('bankAccount')}
											data-cy='choose-bank-account-checkbox'
										/>
									}
									label='Bank Account'
								/>
							)}
						</>
					)}
					{paymentType.creditCard && useCreditCard && (
						<>
							<h3 style={{ marginBottom: 10, marginTop: 40 }}>Choose a Credit Card</h3>
							<Box
								sx={{
									mb: 1,
									display: 'flex',
									flexDirection: 'column',
									justifyContent: 'flex-start',
									width: '30%',
								}}
							>
								{cards?.map((card, index) => {
									const expMonth = card.exp_month || card.expMonth;
									const expYear = card.exp_year || card.expYear;
									return (
										<FormControlLabel
											key={card.id}
											control={
												<Checkbox
													size='small'
													checked={selectedCard === card.id}
													disabled={disableEdit}
													onChange={handlePaymentTypeChange('creditCard', card.id)}
													data-cy={`credit-card-option-checkbox-${index}`}
												/>
											}
											label={
												<Typography variant='body2'>
													{`${card.type} ending in ${card.last4}, ${expMonth}/${expYear}`}
												</Typography>
											}
										/>
									);
								})}
							</Box>
							{mode === 'addCard' ? (
								<Box sx={{ mt: 3, mb: 5, width: '60%' }}>
									<CreditCardForm
										paymentProviderId={paymentProviderId}
										cognitoSub={cognitoSub}
										subscriptionId={subscriptionId}
										tenantSubscriptionId={tenantSubscriptionId}
										gpiFormConfig={gpiFormConfig}
										setShowSnackbar={setShowSnackbar}
										refetchPaymentMethods={refetchPaymentMethods}
									/>
								</Box>
							) : (
								<Button
									variant='outlined'
									color='success'
									sx={{ mt: 3, mb: 3, width: '20%' }}
									size='small'
									disabled={disableEdit}
									onClick={() => setMode('addCard')}
									className='add-new-gpi-card'
									data-cy='add-new-credit-card-button'
								>
									Add New Card
								</Button>
							)}
						</>
					)}

					{paymentType.bankAccount && useAch && (
						<>
							<h3 style={{ marginBottom: 10, marginTop: 40 }}>Choose a Bank Account</h3>
							{showAchComplianceNotice && (
								<Alert
									severity='info'
									sx={{ mt: 1, mb: 2, width: '70%' }}
									data-cy='nacha-admin-compliance-notice'
								>
									By adding a bank account and processing an ACH payment, you agree to comply with
									NACHA&apos;s Operating Rules.
								</Alert>
							)}
							<Box
								sx={{
									mb: 1,
									display: 'flex',
									flexDirection: 'column',
									justifyContent: 'flex-start',
									width: '30%',
								}}
							>
								{bankAccounts?.map((account, index) => (
									<FormControlLabel
										key={account.id}
										control={
											<Checkbox
												size='small'
												checked={selectedBank === account.id}
												disabled={disableEdit}
												onChange={handlePaymentTypeChange('bankAccount', account.id)}
												data-cy={`bank-account-option-checkbox-${index}`}
											/>
										}
										label={
											<Typography variant='body2'>
												{`${account.accountType} account ending in ${account.last4}`}
											</Typography>
										}
									/>
								))}
							</Box>
							{mode === 'addBank' ? (
								<Box sx={{ mt: 5, mb: 5, width: '70%' }}>
									<BankAccountForm
										cognitoSub={cognitoSub}
										setShowSnackbar={setShowSnackbar}
										tenantSubscriptionId={tenantSubscriptionId}
										refetchPaymentMethods={refetchPaymentMethods}
										paymentProviderId={paymentProviderId}
									/>
								</Box>
							) : (
								<Button
									variant='outlined'
									color='success'
									sx={{ mt: 2, mb: 3, width: '20%' }}
									size='small'
									disabled={disableEdit}
									onClick={() => setMode('addBank')}
									className='add-new-gpi-bank-account'
									data-cy='add-new-bank-account-button'
								>
									Add New Bank Account
								</Button>
							)}
						</>
					)}
					{achAuthorization && (
						<AchAuthorizationAcceptance
							authorizations={[achAuthorization]}
							accepted={achAuthorizationAccepted}
							onAcceptedChange={setAchAuthorizationAccepted}
							disabled={disableEdit}
						/>
					)}
				</>
			)}

			<Button
				variant='contained'
				color='primary'
				onClick={handlePaymentUpdate}
				sx={{ mt: 5, width: '20%' }}
				disabled={
					(isEnabled && !selectedCard && !selectedBank) ||
					Boolean(achAuthorization && !achAuthorizationAccepted) ||
					disableEdit
				}
				data-cy='save-button'
			>
				{updateLoading || tenantUpdateLoading ? (
					<CircularProgress size={20} sx={{ color: 'white' }} data-cy='loading-indicator' />
				) : (
					'Save'
				)}
			</Button>
		</Box>
	);
};
